import { writable, derived } from "svelte/store";
import { browser } from "$app/environment";
import { type ComponentType, onMount } from "svelte";

const PANEL_KEY = "sidePanel";

// Get the visible field of the stored state from localStorage
const storedState: { visible: boolean } | null = browser
  ? JSON.parse(window.localStorage?.getItem(PANEL_KEY) || JSON.stringify(PANEL_KEY))
  : null;

export interface PanelState {
  /** The default panel to display for a page */
  defaultPanel: ComponentType | null;
  /** A panel set by a user action */
  panel: ComponentType | null;
  visible: boolean;
  title?: string;
  defaultTitle?: string;
  defaultData: Record<string, any>;
  data: Record<string, any>;
}

const initialState: PanelState = {
  defaultPanel: null,
  panel: null,
  visible: true,
  title: "",
  defaultTitle: "",
  defaultData: {},
  data: {},
};

const sidePanelStore = () => {
  const { subscribe, update } = writable({
    ...initialState,
    visible: storedState?.visible ?? initialState.visible,
  });

  return {
    subscribe,
    update,
    setPageDefault: (panel: ComponentType | null, title: string, data?: Record<string, any>) => {
      // clear aside when page unmounted
      // doesn't use onDestroy because this shouldn't be run in ssr contexts
      onMount(() => () => sidePanel.setPageDefault(null, "", {}));
      if (panel === null) {
        return update((state) => {
          return {
            ...state,
            // if the panel is not visible we want to clear it when setting the default
            panel: state.visible ? state.panel : null,
            defaultPanel: null,
            defaultTitle: "",
            data: {},
            defaultData: {},
          };
        });
      } else {
        return update((state) => ({
          ...state,
          defaultPanel: panel,
          defaultTitle: title,
          defaultData: data || {},
        }));
      }
    },
    setPanel: (panel: ComponentType, title: string, data?: Record<string, any>) =>
      update((state) => ({ ...state, panel, title, data: data || {} })),
    clearPanel: () =>
      update((state) => {
        return state.defaultPanel === null
          ? {
              ...state,
              panel: null,
              title: undefined,
              visible: false,
              data: {},
              defaultData: {},
            }
          : { ...state, panel: null, title: undefined, data: {} };
      }),
    show: () => update((state) => ({ ...state, visible: true })),
    hide: () => update((state) => ({ ...state, visible: false })),
    toggleExpansion: () => update((state) => ({ ...state, visible: !state.visible })),
  };
};

export const sidePanel = sidePanelStore();

// If we're in the browser, let's subscribe to the store
if (browser) {
  sidePanel.subscribe((state) =>
    window.localStorage.setItem(PANEL_KEY, JSON.stringify({ visible: state.visible }))
  );
}

export const currentPanel = derived(sidePanel, ($sidePanel) =>
  $sidePanel.panel
    ? { panel: $sidePanel.panel, data: $sidePanel.data }
    : { panel: $sidePanel.defaultPanel, data: $sidePanel.defaultData }
);

export const isSidePanelVisible = derived(sidePanel, ($sidePanel) => $sidePanel.visible);
export const hasDefault = derived(sidePanel, ($sidePanel) => !!$sidePanel.defaultPanel);
export const hasPanel = derived(sidePanel, ($sidePanel) => !!$sidePanel.panel);
